import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography, TextField, MenuItem, Button, Stack, Alert, IconButton, Chip, Checkbox, FormControlLabel, Divider } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const perspectives = [
  { value: 'neutral', label: 'Neutral' },
  { value: 'progressive', label: 'Progressive' },
  { value: 'conservative', label: 'Conservative' },
  { value: 'libertarian', label: 'Libertarian' },
];

const platforms = [
  { value: 'x', label: 'X / Twitter' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'generic', label: 'Generic' },
];

export default function CivicResponseStudio() {
  const { authenticated, initialized, login } = useAuth();
  const [originalComment, setOriginalComment] = useState(() => { try { return sessionStorage.getItem('civic-comment-draft') || ''; } catch { return ''; } });
  const [perspective, setPerspective] = useState('neutral');
  const [platform, setPlatform] = useState('x');
  const [topicHint, setTopicHint] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [socialReply, setSocialReply] = useState<string | null>(null);
  const [sources, setSources] = useState<any[]>([]);
  const [copyText, setCopyText] = useState<string | null>(null);
  const [includeSourceInCopy, setIncludeSourceInCopy] = useState<boolean>(true);

  // Prefer the actual social reply when present; otherwise fall back to copyText
  const normalizedReply = useMemo(() => {
    const s = (socialReply || '').trim();
    if (s) return s;
    const c = (copyText || '').trim();
    return c;
  }, [socialReply, copyText]);

  const platformLimit = useMemo(() => {
    const p = (platform || 'generic').toLowerCase();
    if (p === 'x' || p === 'twitter') return 280;
    if (p === 'instagram') return 2200;
    if (p === 'reddit') return 10000;
    if (p === 'facebook') return 63206;
    return 500;
  }, [platform]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authenticated) {
      try { sessionStorage.setItem('civic-comment-draft', originalComment); } catch {}
      login(); return;
    }
    setError(null);
    setExplanation(null);
    setSocialReply(null);
    setSources([]);
    if (!originalComment || originalComment.trim().length < 5) {
      setError('Please paste a comment of at least 5 characters.');
      return;
    }
    setLoading(true);
    try {
      const payload: any = { originalComment, perspective, platform };
      if (topicHint && topicHint.trim()) payload.topicHint = topicHint.trim();
      const data = await apiService.postCivicResponseCommentReply(payload);
      setExplanation(data?.explanation || null);
      setSocialReply(data?.socialReply || data?.social_reply || null);
      setCopyText(data?.copyText || data?.copy_text || null);
      setSources(Array.isArray(data?.sources) ? data.sources : []);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to generate a response';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    try { navigator.clipboard?.writeText(text); } catch {}
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Civic Response Studio</Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Paste a social media comment and generate a civil, factual reply with sources. We’ll also include relevant U.S. congressional bills when we can.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <form onSubmit={onSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Original comment"
              value={originalComment}
              onChange={(e) => setOriginalComment(e.target.value)}
              placeholder="Paste the comment you want to reply to"
              multiline minRows={4}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Perspective" value={perspective} onChange={(e) => setPerspective(e.target.value)} fullWidth>
                {perspectives.map(p => (<MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>))}
              </TextField>
              <TextField select label="Platform" value={platform} onChange={(e) => setPlatform(e.target.value)} fullWidth>
                {platforms.map(p => (<MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>))}
              </TextField>
            </Stack>
            <TextField
              label="Topic hint (optional)"
              value={topicHint}
              onChange={(e) => setTopicHint(e.target.value)}
              placeholder="e.g., immigration, healthcare, budget"
              fullWidth
            />
            <Typography variant="caption" color={((socialReply || '').length > platformLimit) ? 'error' : 'textSecondary'}>
              Platform limit: {platformLimit} characters. Your generated reply will be trimmed automatically.
            </Typography>
            <Box>
              <Button type="submit" variant="contained" disabled={loading || !initialized}>{authenticated ? "Generate reply" : "Sign in to generate reply"}</Button>
            </Box>
          </Stack>
        </form>
      </Paper>

      {loading && <LoadingSpinner message="Generating response..." />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {(explanation || normalizedReply) && (
        <Paper sx={{ p: 2 }}>
          {/* Quick copy row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={() => {
                // If including a source link, prefer server-provided copyText (already length-checked)
                const text = includeSourceInCopy ? (copyText || normalizedReply || '') : (normalizedReply || '');
                copyToClipboard(text);
              }}
            >
              Copy for {platform.toUpperCase()}
            </Button>
            <FormControlLabel
              control={<Checkbox size="small" checked={includeSourceInCopy} onChange={(e)=> setIncludeSourceInCopy(e.target.checked)} />}
              label="Include source link if space allows"
            />
            {normalizedReply && (
              <Typography variant="caption" color={(normalizedReply || '').length > platformLimit ? 'error' : 'textSecondary'}>
                {(normalizedReply || '').length}/{platformLimit}
              </Typography>
            )}
          </Box>

          {explanation && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">Explanation</Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{explanation}</Typography>
            </Box>
          )}
          {normalizedReply && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">Social reply</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{normalizedReply}</Typography>
                <IconButton size="small" onClick={() => copyToClipboard(normalizedReply || '')}><ContentCopyIcon fontSize="small" /></IconButton>
              </Box>
            </Box>
          )}
          {Array.isArray(sources) && sources.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Sources</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {sources.map((s, i) => {
                  const url = s.url || '#';
                  const title = s.title || s.ref || url;
                  const type = (s.type || '').toString();
                  const isBill = type === 'bill' || /\/bills\//.test(url || '');
                  return (
                    <Chip key={i} label={title} color={isBill ? 'primary' : 'default'} component="a" clickable href={url} target="_blank" rel="noreferrer" />
                  );
                })}
              </Stack>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
